import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { HDR_PRICING_COPY } from '@rehub/pricing';
import type { HdrEntitlement } from '../services/hdr';
import { openHdrBillingPortal } from '../services/hdr';
import { trackHdrEvent } from '../services/analytics';

export default function HdrUsageCard({
  entitlement,
  compact = false,
}: {
  entitlement: HdrEntitlement | null;
  compact?: boolean;
}) {
  const trackedLow = useRef(false);
  const low = !!entitlement && (entitlement.is_low || entitlement.remaining <= 10);

  useEffect(() => {
    if (!entitlement || !low || trackedLow.current) return;
    trackedLow.current = true;
    trackHdrEvent('hdr_allowance_low', {
      plan: entitlement.plan_id,
      remaining: entitlement.remaining,
      allowance: entitlement.allowance,
    });
  }, [low, entitlement]);

  if (!entitlement) return null;

  const pct = entitlement.allowance > 0
    ? Math.min(100, (entitlement.used / entitlement.allowance) * 100)
    : 0;
  const renews = entitlement.period_end
    ? new Date(entitlement.period_end).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
      })
    : null;

  const manage = async () => {
    try {
      const result = await openHdrBillingPortal();
      if (result?.url) window.location.href = result.url;
    } catch {
      window.location.href = '/pricing';
    }
  };

  if (compact) {
    return (
      <Link
        to="/pricing"
        className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${
          low
            ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
            : 'border-slate-800 bg-slate-900 text-slate-200 hover:border-sky-500/40'
        }`}
        title="HDR Credits"
      >
        <span className="font-semibold tabular-nums">
          {entitlement.remaining}/{entitlement.allowance}
        </span>
        <span className="hidden sm:inline text-slate-400">HDR Credits</span>
      </Link>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-slate-400">{HDR_PRICING_COPY.usageLabel}</p>
          <p className="mt-1 text-2xl font-semibold text-white tabular-nums">
            {entitlement.remaining} / {entitlement.allowance} remaining
          </p>
          <p className="mt-1 text-xs text-slate-500">{entitlement.plan_name}</p>
        </div>
        <button
          type="button"
          onClick={manage}
          className="text-sm font-medium text-sky-300 hover:text-sky-200"
        >
          {HDR_PRICING_COPY.managePlan}
        </button>
      </div>
      <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-slate-800">
        <div
          className={`h-full rounded-full transition-all ${low ? 'bg-amber-400' : 'bg-sky-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-slate-400">
        {renews && <span>{HDR_PRICING_COPY.renews(renews)}</span>}
        {low && (
          <Link to="/pricing" className="text-amber-300 hover:text-amber-200">
            {HDR_PRICING_COPY.remainingThisMonth(entitlement.remaining)} — upgrade
          </Link>
        )}
        {entitlement.status === 'past_due' && (
          <button type="button" onClick={manage} className="text-amber-300">
            Payment failed — update billing
          </button>
        )}
      </div>
    </div>
  );
}
